import { ExpandMore } from "@mui/icons-material";
import { Button, Checkbox, Collapse, Grid, TableCell, TableRow, Typography } from "@mui/material";
import Tooltip from "../../../Atoms/Tooltip";
import { format } from "date-fns";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { formatCurrency, formatDocument } from "../../../../Utils/formatters";
import {
  CollapseContainer,
  CollapseContent,
  CustomTableRow,
  ExpandMoreButton,
  StyledH2
} from "../styles";

interface RowProps {
  transaction: any;
  disabled?: boolean;
}

export const InvoiceRow = ({ transaction, disabled }: RowProps) => {
  const [expanded, setExpanded] = useState(false);
  const history = useHistory();

  const handleExpandClick = () => setExpanded(!expanded);

  const checkIfValueIsNull = (value: string | null | undefined) => {
    return value ?? "-";
  };

  const handleClick = () => {
    history.push({
      pathname: `/anticipation/duplicates`,
      state: {
        invoiceIds: [transaction.anticipationId],
      },
    });
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case "Enviada":
        return { background: "#E0F7FA", color: "#00796B" };
      case "Rejeitada":
        return { background: "#FFEBEE", color: "#C62828" };
      case "Inválida":
        return { background: "#FFF3E0", color: "#EF6C00" };
      case "Aprovada":
        return { background: "#E8F5E9", color: "#2E7D32" };
      case "Antecipada":
        return { background: "#E3F2FD", color: "#1565C0" };
      case "Validada":
        return { background: "#F3E5F5", color: "#6A1B9A" };
      case "Aguardando Validação":
        return { background: "#EEEEEE", color: "#5B5B5B" };
      default:
        return { background: "#E0E0E0", color: "#424242" };
    }
  };

  return (
    <>
      <CustomTableRow
        onClick={disabled ? undefined : handleClick}
        sx={{
          cursor: disabled ? "default" : "pointer",
          backgroundColor: disabled ? "#f0f0f0" : "inherit",
          "&:hover": disabled ? undefined : {
            backgroundColor: "#f5f5f5",
          },
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}>
        <TableCell>
          <ExpandMoreButton
            expand={expanded}
            onClick={(e) => {
              if (disabled) return;
              e.stopPropagation();
              handleExpandClick();
            }}
            disabled={disabled}
            aria-expanded={expanded}
            aria-label="Mostrar detalhes da nota"
            title="Mostrar detalhes da nota"
          >
            <ExpandMore />
          </ExpandMoreButton>
        </TableCell>
        <TableCell>
          <Typography
            sx={{
              background: getStatusColor(transaction.status?.name).background,
              color: getStatusColor(transaction.status?.name).color,
              px: 2,
              py: 0.5,
              borderRadius: '16px',
              fontWeight: 500,
              fontSize: '0.75rem',
              display: 'inline-block',
              width: 'fit-content'
            }}
          >
            {checkIfValueIsNull(transaction.status?.name)}
          </Typography>
          <Typography>
            {checkIfValueIsNull(transaction.protocol)}
          </Typography>       
        </TableCell>
        <TableCell>
          {transaction.registerDate
            ? format(new Date(transaction.registerDate), "dd/MM/yyyy")
            : "-"}
        </TableCell>
        <TableCell>{formatCurrency(transaction.amount, "BRL", 0)}</TableCell>
        <TableCell>{transaction.customer?.tradeName?.split(" ")[0] ?? ""}</TableCell>
        <TableCell>{transaction.invoiceNumber ?? ""}</TableCell>
        <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Tooltip title={transaction.key || "-"}>
            <span style={{ fontSize: "0.85em", display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {checkIfValueIsNull(transaction.key)}
            </span>
          </Tooltip>
        </TableCell>
      </CustomTableRow>

      <TableRow>
        <TableCell colSpan={9} style={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <CollapseContainer>
              <StyledH2>Detalhes</StyledH2>
              <CollapseContent>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <p>
                      <strong>Destinatário:</strong>
                      <Tooltip title={checkIfValueIsNull(transaction.customer?.tradeName)}>
                        <span
                          style={{
                            display: "block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                          }}
                        >
                          {checkIfValueIsNull(transaction.customer?.tradeName)}
                        </span>
                      </Tooltip>
                    </p>
                    <p>
                      <strong>Documento:</strong>
                      <span>
                        {formatDocument(
                          transaction.customer?.nationalRegistration,
                          transaction.customer?.nationalRegistration && transaction.customer.nationalRegistration.length > 11
                            ? "CNPJ_COMPACT"
                            : "CPF"
                        )}
                      </span>
                    </p>
                  </Grid>
                  <Grid item xs={4}>
                    <p>
                      <strong>Inscrição Estadual:</strong>
                      <span>{checkIfValueIsNull(transaction.customer?.ie)}</span>
                    </p>
                    <p>
                      <strong>Tipo:</strong>
                      <span>{checkIfValueIsNull(transaction.type)}</span>
                    </p>
                  </Grid>
                </Grid>
              </CollapseContent>
              <Button sx={{ mt: 1 }}
                disabled={disabled}
                onClick={handleClick}>
                Mais detalhes
              </Button>
            </CollapseContainer>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
